/**
 * Scoring & standings service — I/O wrapper around `scoringEngine`.
 *
 * Score events are append-only history; the engine derives every result and
 * standings table from them. The "submit" step writes a denormalized
 * GameResult so we can lock and audit. Standings are recomputed from results
 * any time a game is submitted/edited.
 */

import type {
  Game,
  GameResult,
  GameRoster,
  GameScoringSession,
  Id,
  Iso8601,
  Profile,
  ScoreEvent,
  StandingsRecord,
} from '../models';
import { getDb, updateDb } from './store';
import { newId, nowIso } from './ids';
import { canScoreGame } from '../permissions/permissions';
import {
  findGameWinnerEventId,
  tallyGameEvents,
  computeStandings,
  computePlayerStats,
  computeBracketSeeds,
  type PlayerStat,
  type BracketSeed,
} from './scoringEngine';

const SCORING_SESSION_TTL_MS = 2 * 60 * 1000;

// --------------------------------------------------------------------------
// Reads
// --------------------------------------------------------------------------

export function listScoreEvents(gameId: Id): ScoreEvent[] {
  return getDb()
    .scoreEvents.filter((e) => e.gameId === gameId)
    .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
}

export function getResultForGame(gameId: Id): GameResult | undefined {
  return getDb().gameResults.find((r) => r.gameId === gameId);
}

export function getStandings(tournamentId: Id, divisionId?: Id): StandingsRecord[] {
  return getDb()
    .standings.filter(
      (s) => s.tournamentId === tournamentId && (!divisionId || s.divisionId === divisionId),
    )
    .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
}

/** Live tally — does NOT require GameResult to exist. */
export function getLiveScore(gameId: Id): { home: number; away: number } {
  const game = getDb().games.find((g) => g.id === gameId);
  if (!game) return { home: 0, away: 0 };
  const events = listScoreEvents(gameId);
  const t = tallyGameEvents(events, game);
  return { home: t.homeScore, away: t.awayScore };
}

export function canProfileScoreGame(profile: Profile | null | undefined, gameId: Id): boolean {
  const db = getDb();
  return canScoreGame(profile, gameId, { gameAssignments: db.gameAssignments });
}

export function startGame(gameId: Id): Game | undefined {
  const db = getDb();
  const game = db.games.find((g) => g.id === gameId);
  if (!game || game.status === 'final' || game.status === 'forfeit' || game.status === 'cancelled') return game;
  const ts = nowIso();
  updateDb((current) => ({
    ...current,
    games: current.games.map((row) =>
      row.id === gameId ? { ...row, status: 'in_progress', updatedAt: ts } : row,
    ),
    gameRosters: current.gameRosters.map((row) =>
      row.gameId === gameId && !row.isLocked ? { ...row, isLocked: true, lockedAt: ts, updatedAt: ts } : row,
    ),
  }));
  return getDb().games.find((row) => row.id === gameId);
}

export function getActiveScoringSession(gameId: Id): GameScoringSession | undefined {
  const now = Date.now();
  return getDb()
    .gameScoringSessions
    .filter((session) => session.gameId === gameId && session.status === 'active')
    .sort((a, b) => b.lastHeartbeatAt.localeCompare(a.lastHeartbeatAt))
    .find((session) => new Date(session.expiresAt).getTime() > now);
}

export function startScoringSession(input: {
  gameId: Id;
  profileId: Id;
  deviceLabel?: string;
  forceTakeover?: boolean;
}): GameScoringSession {
  const ts = nowIso();
  const active = getActiveScoringSession(input.gameId);
  const sameOwner = active?.profileId === input.profileId;
  if (active && sameOwner) {
    heartbeatScoringSession(active.sessionId);
    return active;
  }
  if (active && !sameOwner && !input.forceTakeover) {
    return active;
  }
  const expiresAt = new Date(Date.now() + SCORING_SESSION_TTL_MS).toISOString();
  const session: GameScoringSession = {
    id: newId('gss'),
    kind: 'game-scoring-session',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    profileId: input.profileId,
    sessionId: newId('session'),
    status: 'active',
    startedAt: ts,
    lastHeartbeatAt: ts,
    expiresAt,
    deviceLabel: input.deviceLabel,
  };
  updateDb((db) => ({
    ...db,
    gameScoringSessions: [
      ...db.gameScoringSessions.map((existing) =>
        existing.gameId === input.gameId && existing.status === 'active'
          ? ({
              ...existing,
              status: input.forceTakeover && existing.profileId !== input.profileId ? 'taken_over' : 'released',
              endedAt: ts,
              takenOverByProfileId: input.forceTakeover ? input.profileId : existing.takenOverByProfileId,
              updatedAt: ts,
            } satisfies GameScoringSession)
          : existing,
      ),
      session,
    ],
  }));
  return session;
}

export function heartbeatScoringSession(sessionId: Id): void {
  const ts = nowIso();
  const expiresAt = new Date(Date.now() + SCORING_SESSION_TTL_MS).toISOString();
  updateDb((db) => ({
    ...db,
    gameScoringSessions: db.gameScoringSessions.map((session) =>
      session.sessionId === sessionId && session.status === 'active'
        ? { ...session, lastHeartbeatAt: ts, expiresAt, updatedAt: ts }
        : session,
    ),
  }));
}

export function releaseScoringSession(sessionId: Id): void {
  const ts = nowIso();
  updateDb((db) => ({
    ...db,
    gameScoringSessions: db.gameScoringSessions.map((session) =>
      session.sessionId === sessionId && session.status === 'active'
        ? { ...session, status: 'released', endedAt: ts, updatedAt: ts }
        : session,
    ),
  }));
}

export type AttendanceStatus = NonNullable<GameRoster['attendanceByPlayerId']>[Id];

export function setGameAttendance(input: {
  gameId: Id;
  teamId: Id;
  playerId: Id;
  status: AttendanceStatus;
  jerseyNumber?: number;
  position?: string;
  note?: string;
  profileId?: Id;
}): GameRoster | undefined {
  const db = getDb();
  const game = db.games.find((g) => g.id === input.gameId);
  if (!game) return undefined;
  const ts = nowIso();
  const existing = db.gameRosters.find((row) => row.gameId === input.gameId && row.teamId === input.teamId);
  const rosterEntry = db.rosterEntries.find((entry) => entry.teamId === input.teamId && entry.playerId === input.playerId);
  const next: GameRoster = {
    id: existing?.id ?? newId('grs'),
    kind: 'game-roster',
    createdAt: existing?.createdAt ?? ts,
    updatedAt: ts,
    gameId: input.gameId,
    teamId: input.teamId,
    playerIds: Array.from(new Set([...(existing?.playerIds ?? []), input.playerId])),
    jerseyByPlayerId: {
      ...(existing?.jerseyByPlayerId ?? {}),
      [input.playerId]: input.jerseyNumber ?? rosterEntry?.jerseyNumber ?? 0,
    },
    positionByPlayerId: {
      ...(existing?.positionByPlayerId ?? {}),
      [input.playerId]: input.position ?? rosterEntry?.position ?? '',
    },
    startingGoalieId: existing?.startingGoalieId,
    attendanceByPlayerId: {
      ...(existing?.attendanceByPlayerId ?? {}),
      [input.playerId]: input.status,
    },
    attendanceNotesByPlayerId: {
      ...(existing?.attendanceNotesByPlayerId ?? {}),
      ...(input.note ? { [input.playerId]: input.note } : {}),
    },
    isLocked: existing?.isLocked ?? false,
    lockedAt: existing?.lockedAt,
    finalizedByProfileId: existing?.finalizedByProfileId,
  };
  updateDb((current) => ({
    ...current,
    gameRosters: [...current.gameRosters.filter((row) => row.id !== next.id), next],
  }));
  return next;
}

// --------------------------------------------------------------------------
// Score event mutations
// --------------------------------------------------------------------------

export interface AddGoalInput {
  gameId: Id;
  teamId: Id;
  scorerPlayerId?: Id;
  assistPlayerId?: Id;
  gameSecond?: number;
  period?: number;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
  isPowerPlay?: boolean;
  isShortHanded?: boolean;
  isEmptyNet?: boolean;
  notes?: string;
}

export function addGoal(input: AddGoalInput): { goal: ScoreEvent; assist?: ScoreEvent } {
  const ts = nowIso();
  const recordedAt: Iso8601 = ts;
  const goal: ScoreEvent = {
    id: newId('se'),
    kind: 'score-event',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    teamId: input.teamId,
    playerId: input.scorerPlayerId,
    type: 'goal',
    recordedAt,
    sourceSessionId: input.sourceSessionId,
    createdByProfileId: input.createdByProfileId,
    gameSecond: input.gameSecond,
    period: input.period,
    isPowerPlay: input.isPowerPlay,
    isShortHanded: input.isShortHanded,
    isEmptyNet: input.isEmptyNet,
    notes: input.notes,
  };
  let assist: ScoreEvent | undefined;
  if (input.assistPlayerId) {
    assist = {
      id: newId('se'),
      kind: 'score-event',
      createdAt: ts,
      updatedAt: ts,
      gameId: input.gameId,
      teamId: input.teamId,
      playerId: input.assistPlayerId,
      type: 'assist',
      recordedAt,
      sourceSessionId: input.sourceSessionId,
      createdByProfileId: input.createdByProfileId,
      parentEventId: goal.id,
      period: input.period,
    };
  }
  updateDb((db) => ({
    ...db,
    scoreEvents: [...db.scoreEvents, goal, ...(assist ? [assist] : [])],
    games: db.games.map((g) =>
      g.id === input.gameId && (g.status === 'scheduled' || g.status === 'ready')
        ? { ...g, status: 'in_progress', updatedAt: ts }
        : g,
    ),
  }));
  return { goal, assist };
}

export interface UpdateGoalInput {
  goalEventId: Id;
  teamId: Id;
  scorerPlayerId?: Id;
  assistPlayerId?: Id;
  gameSecond?: number;
  period?: number;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
  isPowerPlay?: boolean;
  isShortHanded?: boolean;
  isEmptyNet?: boolean;
  notes?: string;
}

export function updateGoal(input: UpdateGoalInput): { goal?: ScoreEvent; assist?: ScoreEvent } {
  const db = getDb();
  const currentGoal = db.scoreEvents.find((event) => event.id === input.goalEventId && event.type === 'goal');
  if (!currentGoal) return {};
  const game = db.games.find((row) => row.id === currentGoal.gameId);
  if (!game || game.status === 'final' || game.status === 'forfeit') return {};
  const ts = nowIso();
  const currentAssist = db.scoreEvents.find(
    (event) => event.parentEventId === currentGoal.id && event.type === 'assist',
  );
  const assistPlayerId =
    input.assistPlayerId && input.assistPlayerId !== input.scorerPlayerId ? input.assistPlayerId : undefined;
  const nextAssist: ScoreEvent | undefined = assistPlayerId
    ? {
        id: currentAssist?.id ?? newId('se'),
        kind: 'score-event',
        createdAt: currentAssist?.createdAt ?? ts,
        updatedAt: ts,
        gameId: currentGoal.gameId,
        teamId: input.teamId,
        playerId: assistPlayerId,
        type: 'assist',
        recordedAt: currentAssist?.recordedAt ?? currentGoal.recordedAt,
        sourceSessionId: input.sourceSessionId ?? currentAssist?.sourceSessionId,
        createdByProfileId: input.createdByProfileId ?? currentAssist?.createdByProfileId,
        parentEventId: currentGoal.id,
        period: input.period,
        gameSecond: input.gameSecond,
      }
    : undefined;
  const nextGoal: ScoreEvent = {
    ...currentGoal,
    updatedAt: ts,
    teamId: input.teamId,
    playerId: input.scorerPlayerId,
    sourceSessionId: input.sourceSessionId ?? currentGoal.sourceSessionId,
    createdByProfileId: input.createdByProfileId ?? currentGoal.createdByProfileId,
    gameSecond: input.gameSecond,
    period: input.period,
    isPowerPlay: input.isPowerPlay,
    isShortHanded: input.isShortHanded,
    isEmptyNet: input.isEmptyNet,
    notes: input.notes,
  };
  updateDb((current) => ({
    ...current,
    scoreEvents: [
      ...current.scoreEvents
        .filter((event) => event.id !== nextGoal.id && event.id !== currentAssist?.id)
        .filter((event) => !(event.parentEventId === nextGoal.id && event.type === 'assist')),
      nextGoal,
      ...(nextAssist ? [nextAssist] : []),
    ],
  }));
  return { goal: nextGoal, assist: nextAssist };
}

export interface AddPenaltyShotInput {
  gameId: Id;
  teamId: Id;
  shooterPlayerId?: Id;
  made: boolean;
  period?: number;
  gameSecond?: number;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
}

export function addPenaltyShot(input: AddPenaltyShotInput): ScoreEvent {
  const ts = nowIso();
  const ev: ScoreEvent = {
    id: newId('se'),
    kind: 'score-event',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    teamId: input.teamId,
    playerId: input.shooterPlayerId,
    type: input.made ? 'penalty_shot_made' : 'penalty_shot_missed',
    recordedAt: ts,
    sourceSessionId: input.sourceSessionId,
    createdByProfileId: input.createdByProfileId,
    period: input.period,
    gameSecond: input.gameSecond,
  };
  updateDb((db) => ({
    ...db,
    scoreEvents: [...db.scoreEvents, ev],
    games: db.games.map((g) =>
      g.id === input.gameId && (g.status === 'scheduled' || g.status === 'ready')
        ? { ...g, status: 'in_progress', updatedAt: ts }
        : g,
    ),
  }));
  return ev;
}

export function deleteScoreEvent(eventId: Id): void {
  const db = getDb();
  const event = db.scoreEvents.find((row) => row.id === eventId);
  const game = event ? db.games.find((row) => row.id === event.gameId) : undefined;
  if (!event || !game || game.status === 'final' || game.status === 'forfeit') return;
  updateDb((db) => ({
    ...db,
    // Also drop a linked assist when its parent goal is removed.
    scoreEvents: db.scoreEvents.filter(
      (e) => e.id !== eventId && e.parentEventId !== eventId,
    ),
  }));
}

export interface AddPenaltyInput {
  gameId: Id;
  teamId: Id;
  playerId?: Id;
  penaltyMinutes: number;
  penaltyType?: string;
  penaltyClass?: import('../models').PenaltyClassification;
  period?: number;
  gameSecond?: number;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
  notes?: string;
}

export function addPenalty(input: AddPenaltyInput): ScoreEvent {
  const ts = nowIso();
  const ev: ScoreEvent = {
    id: newId('se'),
    kind: 'score-event',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    teamId: input.teamId,
    playerId: input.playerId,
    type: 'penalty',
    recordedAt: ts,
    sourceSessionId: input.sourceSessionId,
    createdByProfileId: input.createdByProfileId,
    period: input.period,
    gameSecond: input.gameSecond,
    penaltyMinutes: input.penaltyMinutes,
    penaltyType: input.penaltyType,
    penaltyClass: input.penaltyClass,
    notes: input.notes,
  };
  updateDb((db) => ({
    ...db,
    scoreEvents: [...db.scoreEvents, ev],
    games: db.games.map((g) =>
      g.id === input.gameId && (g.status === 'scheduled' || g.status === 'ready')
        ? { ...g, status: 'in_progress', updatedAt: ts }
        : g,
    ),
  }));
  return ev;
}

export interface UpdatePenaltyInput {
  penaltyEventId: Id;
  teamId: Id;
  playerId?: Id;
  penaltyMinutes: number;
  penaltyType?: string;
  penaltyClass?: import('../models').PenaltyClassification;
  period?: number;
  gameSecond?: number;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
  notes?: string;
}

export function updatePenalty(input: UpdatePenaltyInput): ScoreEvent | undefined {
  const db = getDb();
  const currentPenalty = db.scoreEvents.find((event) => event.id === input.penaltyEventId && event.type === 'penalty');
  if (!currentPenalty) return undefined;
  const game = db.games.find((row) => row.id === currentPenalty.gameId);
  if (!game || game.status === 'final' || game.status === 'forfeit') return undefined;
  const ts = nowIso();
  const nextPenalty: ScoreEvent = {
    ...currentPenalty,
    updatedAt: ts,
    teamId: input.teamId,
    playerId: input.playerId,
    sourceSessionId: input.sourceSessionId ?? currentPenalty.sourceSessionId,
    createdByProfileId: input.createdByProfileId ?? currentPenalty.createdByProfileId,
    period: input.period,
    gameSecond: input.gameSecond,
    penaltyMinutes: input.penaltyMinutes,
    penaltyType: input.penaltyType,
    penaltyClass: input.penaltyClass,
    notes: input.notes,
  };
  updateDb((current) => ({
    ...current,
    scoreEvents: current.scoreEvents.map((event) => (event.id === nextPenalty.id ? nextPenalty : event)),
  }));
  return nextPenalty;
}

export interface AddShotInput {
  gameId: Id;
  teamId: Id;
  playerId?: Id;
  period?: number;
  gameSecond?: number;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
  /** True if the shot was saved (records both shot_on_goal and a save event). */
  saved?: boolean;
  goalieId?: Id;
}

export function addShotOnGoal(input: AddShotInput): ScoreEvent {
  const ts = nowIso();
  const ev: ScoreEvent = {
    id: newId('se'),
    kind: 'score-event',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    teamId: input.teamId,
    playerId: input.playerId,
    type: 'shot_on_goal',
    recordedAt: ts,
    sourceSessionId: input.sourceSessionId,
    createdByProfileId: input.createdByProfileId,
    period: input.period,
    gameSecond: input.gameSecond,
    goalieId: input.goalieId,
  };
  const events: ScoreEvent[] = [ev];
  if (input.saved && input.goalieId) {
    events.push({
      id: newId('se'),
      kind: 'score-event',
      createdAt: ts,
      updatedAt: ts,
      gameId: input.gameId,
      // Save is credited to the goalie's team (opposite of shooter).
      playerId: input.goalieId,
      type: 'save',
      recordedAt: ts,
      sourceSessionId: input.sourceSessionId,
      createdByProfileId: input.createdByProfileId,
      period: input.period,
      gameSecond: input.gameSecond,
      goalieId: input.goalieId,
      parentEventId: ev.id,
    });
  }
  updateDb((db) => ({
    ...db,
    scoreEvents: [...db.scoreEvents, ...events],
    games: db.games.map((g) =>
      g.id === input.gameId && (g.status === 'scheduled' || g.status === 'ready')
        ? { ...g, status: 'in_progress', updatedAt: ts }
        : g,
    ),
  }));
  return ev;
}

export interface AddShootoutAttemptInput {
  gameId: Id;
  teamId: Id;
  shooterPlayerId?: Id;
  goalieId?: Id;
  made: boolean;
  round?: number;
  attemptNumber?: number;
  suddenDeath?: boolean;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
}

export function addShootoutAttempt(input: AddShootoutAttemptInput): ScoreEvent {
  const ts = nowIso();
  const ev: ScoreEvent = {
    id: newId('se'),
    kind: 'score-event',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    teamId: input.teamId,
    playerId: input.shooterPlayerId,
    type: 'shootout_attempt',
    recordedAt: ts,
    sourceSessionId: input.sourceSessionId,
    createdByProfileId: input.createdByProfileId,
    period: 5,
    goalieId: input.goalieId,
    shootoutRound: input.round,
    shootoutAttemptNumber: input.attemptNumber,
    shootoutMade: input.made,
    shootoutSuddenDeath: input.suddenDeath,
    notes: input.made ? 'made' : 'missed',
  };
  updateDb((db) => ({
    ...db,
    scoreEvents: [...db.scoreEvents, ev],
    games: db.games.map((g) =>
      g.id === input.gameId && (g.status === 'scheduled' || g.status === 'ready')
        ? { ...g, status: 'in_progress', updatedAt: ts }
        : g,
    ),
  }));
  return ev;
}

export function addScorekeeperNote(input: {
  gameId: Id;
  notes: string;
  teamId?: Id;
  period?: number;
  gameSecond?: number;
  sourceSessionId?: Id;
  createdByProfileId?: Id;
}): ScoreEvent {
  const ts = nowIso();
  const ev: ScoreEvent = {
    id: newId('se'),
    kind: 'score-event',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    teamId: input.teamId,
    type: 'note',
    recordedAt: ts,
    period: input.period,
    gameSecond: input.gameSecond,
    sourceSessionId: input.sourceSessionId,
    createdByProfileId: input.createdByProfileId,
    notes: input.notes,
  };
  updateDb((db) => ({
    ...db,
    scoreEvents: [...db.scoreEvents, ev],
  }));
  return ev;
}

// --------------------------------------------------------------------------
// Result lifecycle
// --------------------------------------------------------------------------

export interface SubmitResultInput {
  gameId: Id;
  decidedBy?: GameResult['decidedBy'];
  winningTeamId?: Id;
  submittedByProfileId?: Id;
}

export interface SubmitChecklist {
  blockers: string[];
  warnings: string[];
}

export function buildSubmitChecklist(gameId: Id, input: { decidedBy?: GameResult['decidedBy']; winningTeamId?: Id } = {}): SubmitChecklist {
  const db = getDb();
  const game = db.games.find((g) => g.id === gameId);
  if (!game) return { blockers: ['Game not found.'], warnings: [] };
  const events = db.scoreEvents.filter((e) => e.gameId === gameId);
  const tally = tallyGameEvents(events, game);
  const rosters = db.gameRosters.filter((row) => row.gameId === gameId);
  const blockers: string[] = [];
  const warnings: string[] = [];
  if (game.status === 'final' || game.status === 'forfeit') {
    blockers.push('This game is already final. Reopen it before submitting changes.');
  }
  if (input.decidedBy === 'forfeit' && !input.winningTeamId) {
    blockers.push('Choose the team receiving the forfeit win.');
  }
  if (tally.homeScore === tally.awayScore && input.decidedBy && input.decidedBy !== 'regulation' && input.decidedBy !== 'forfeit' && !input.winningTeamId) {
    blockers.push('Choose the team that won the overtime or shootout.');
  }
  if (tally.homeScore === tally.awayScore && input.decidedBy === 'shootout') {
    const shootoutAttempts = events.filter((event) => event.type === 'shootout_attempt');
    if (shootoutAttempts.length === 0) {
      blockers.push('Record the shootout attempts before submitting a shootout final.');
    }
    if (input.winningTeamId) {
      const winnerGoals = input.winningTeamId === game.homeTeamId ? tally.homeShootoutGoals : tally.awayShootoutGoals;
      const loserGoals = input.winningTeamId === game.homeTeamId ? tally.awayShootoutGoals : tally.homeShootoutGoals;
      if (shootoutAttempts.length > 0 && winnerGoals <= loserGoals) {
        blockers.push('Shootout attempts must show the selected winner ahead.');
      }
    }
  }
  if (events.length === 0 && input.decidedBy !== 'forfeit') warnings.push('No scoring events have been recorded.');
  if (input.decidedBy !== 'forfeit' && (tally.homeShots < tally.homeScore || tally.awayShots < tally.awayScore)) {
    warnings.push('Shots are lower than goals. Add shots if your league tracks them.');
  }
  if (input.decidedBy !== 'forfeit' && (rosters.length === 0 || rosters.every((row) => Object.keys(row.attendanceByPlayerId ?? {}).length === 0))) {
    warnings.push('Attendance has not been confirmed.');
  }
  return { blockers, warnings };
}

export function submitGameResult(input: SubmitResultInput): GameResult | undefined {
  const db = getDb();
  const game = db.games.find((g) => g.id === input.gameId);
  if (!game) return undefined;
  if (input.decidedBy === 'forfeit') {
    if (!input.winningTeamId) return undefined;
    return submitForfeitResult({
      gameId: input.gameId,
      winningTeamId: input.winningTeamId,
      submittedByProfileId: input.submittedByProfileId,
    });
  }
  const checklist = buildSubmitChecklist(input.gameId, input);
  if (checklist.blockers.length > 0) return undefined;
  const events = db.scoreEvents.filter((e) => e.gameId === input.gameId);
  const tally = tallyGameEvents(events, game);
  const ts = nowIso();
  const decidedBy = input.decidedBy ?? 'regulation';
  const winningTeamId =
    input.winningTeamId ??
    (tally.homeScore > tally.awayScore
      ? game.homeTeamId
        : tally.awayScore > tally.homeScore
          ? game.awayTeamId
          : decidedBy === 'shootout'
          ? tally.homeShootoutGoals > tally.awayShootoutGoals
            ? game.homeTeamId
            : tally.awayShootoutGoals > tally.homeShootoutGoals
              ? game.awayTeamId
              : undefined
          : undefined);
  let homeScore = tally.homeScore;
  let awayScore = tally.awayScore;
  if (homeScore === awayScore && winningTeamId && decidedBy !== 'regulation') {
    if (winningTeamId === game.homeTeamId) homeScore += 1;
    if (winningTeamId === game.awayTeamId) awayScore += 1;
  }
  const result: GameResult = {
    id: db.gameResults.find((r) => r.gameId === input.gameId)?.id ?? newId('gr'),
    kind: 'game-result',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    homeScore,
    awayScore,
    endedAt: ts,
    submittedByProfileId: input.submittedByProfileId,
    decidedBy,
    winningTeamId,
    preDecisionHomeScore: tally.homeScore,
    preDecisionAwayScore: tally.awayScore,
    shootoutHomeGoals: tally.homeShootoutGoals,
    shootoutAwayGoals: tally.awayShootoutGoals,
    shootoutAttempts: tally.shootoutAttempts,
  };
  const gameWinnerEventId = findGameWinnerEventId(events, game, result);
  updateDb((d) => ({
    ...d,
    scoreEvents: d.scoreEvents.map((event) =>
      event.gameId === input.gameId
        ? { ...event, isGameWinner: event.id === gameWinnerEventId, updatedAt: event.id === gameWinnerEventId ? ts : event.updatedAt }
        : event,
    ),
    gameResults: [...d.gameResults.filter((r) => r.gameId !== input.gameId), result],
    games: d.games.map((g) =>
      g.id === input.gameId ? { ...g, status: 'final', updatedAt: ts } : g,
    ),
  }));
  recomputeStandings(game.tournamentId);
  return result;
}

export interface SubmitForfeitInput {
  gameId: Id;
  winningTeamId: Id;
  submittedByProfileId?: Id;
}

export function submitForfeitResult(input: SubmitForfeitInput): GameResult | undefined {
  const db = getDb();
  const game = db.games.find((g) => g.id === input.gameId);
  if (!game || (input.winningTeamId !== game.homeTeamId && input.winningTeamId !== game.awayTeamId)) return undefined;
  const ts = nowIso();
  const homeWon = input.winningTeamId === game.homeTeamId;
  const result: GameResult = {
    id: db.gameResults.find((r) => r.gameId === input.gameId)?.id ?? newId('gr'),
    kind: 'game-result',
    createdAt: ts,
    updatedAt: ts,
    gameId: input.gameId,
    homeScore: homeWon ? 1 : 0,
    awayScore: homeWon ? 0 : 1,
    endedAt: ts,
    submittedByProfileId: input.submittedByProfileId,
    decidedBy: 'forfeit',
    winningTeamId: input.winningTeamId,
  };
  updateDb((d) => ({
    ...d,
    gameResults: [...d.gameResults.filter((r) => r.gameId !== input.gameId), result],
    games: d.games.map((g) => (g.id === input.gameId ? { ...g, status: 'forfeit', updatedAt: ts } : g)),
  }));
  recomputeStandings(game.tournamentId);
  return result;
}

export function reopenGame(gameId: Id): void {
  const ts = nowIso();
  updateDb((db) => ({
    ...db,
    games: db.games.map((g) => (g.id === gameId ? { ...g, status: 'in_progress', updatedAt: ts } : g)),
    gameResults: db.gameResults.filter((r) => r.gameId !== gameId),
  }));
  const tournamentId = getDb().games.find((g) => g.id === gameId)?.tournamentId;
  if (tournamentId) recomputeStandings(tournamentId);
}

export function createGameArchiveMetadata(input: {
  gameId: Id;
  objectKey?: string;
  createdByProfileId?: Id;
}): void {
  const db = getDb();
  const game = db.games.find((g) => g.id === input.gameId);
  if (!game) return;
  const result = db.gameResults.find((r) => r.gameId === game.id);
  const ts = nowIso();
  const objectKey = input.objectKey ?? `archives/games/${game.id}/${ts.replace(/[:.]/g, '-')}.json`;
  updateDb((current) => ({
    ...current,
    gameArchives: [
      ...current.gameArchives,
      {
        id: newId('ga'),
        kind: 'game-archive',
        createdAt: ts,
        updatedAt: ts,
        gameId: game.id,
        tournamentId: game.tournamentId,
        resultId: result?.id,
        objectKey,
        contentType: 'application/json',
        createdByProfileId: input.createdByProfileId,
        archivedAt: ts,
        status: 'pending',
      },
    ],
    gameResults: result
      ? current.gameResults.map((row) =>
          row.id === result.id ? { ...row, archiveKey: objectKey, updatedAt: ts } : row,
        )
      : current.gameResults,
  }));
}

// --------------------------------------------------------------------------
// Standings
// --------------------------------------------------------------------------

export function recomputeStandings(tournamentId: Id): StandingsRecord[] {
  const db = getDb();
  const tournament = db.tournaments.find((t) => t.id === tournamentId);
  if (!tournament) return [];
  const records = computeStandings({
    tournamentId,
    divisions: db.divisions.filter((d) => d.tournamentId === tournamentId),
    teams: db.teams.filter((t) => t.tournamentId === tournamentId),
    games: db.games.filter((g) => g.tournamentId === tournamentId),
    results: db.gameResults,
    events: db.scoreEvents,
    settings: tournament.settings,
  });
  updateDb((d) => ({
    ...d,
    standings: [...d.standings.filter((s) => s.tournamentId !== tournamentId), ...records],
  }));
  return records;
}

export function getPlayerStats(tournamentId: Id): PlayerStat[] {
  const db = getDb();
  const games = db.games.filter((g) => g.tournamentId === tournamentId);
  const gameIds = new Set(games.map((g) => g.id));
  const events = db.scoreEvents.filter((e) => gameIds.has(e.gameId));
  const rosterEntries = db.rosterEntries
    .filter((r) => r.tournamentId === tournamentId)
    .map(({ teamId, playerId }) => ({ teamId, playerId }));
  return computePlayerStats({
    tournamentId,
    games,
    events,
    rosterEntries,
    gameRosters: db.gameRosters.filter((row) => gameIds.has(row.gameId)),
  });
}

export function getBracketSeeds(tournamentId: Id): BracketSeed[] {
  const standings = getStandings(tournamentId);
  return computeBracketSeeds(standings);
}

// Accept Game just to tighten typing for callers.
export type { Game };
